import { Link } from "react-router-dom";
import { ArrowRight, BadgeCheck } from "lucide-react";
import Reveal from "@/components/layout/Reveal";
import SectionLabel from "@/components/layout/SectionLabel";

export const LeadingTheWaySection = () => {
  return (
    <section data-testid="leading-the-way" className="py-20 sm:py-28 bg-[#F8FAFC]">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 grid lg:grid-cols-12 gap-12 items-center">
        {/* Copy */}
        <Reveal className="lg:col-span-6">
          <SectionLabel>Innovation</SectionLabel>
          <h2 className="font-heading mt-4 text-3xl sm:text-5xl font-bold tracking-tight text-[#0B132B] leading-[1.05]">
            Leading the way in <span className="text-[#10B981]">hot water innovation.</span>
          </h2>
          <p className="mt-5 text-lg text-[#475569] leading-relaxed">
            The NeoPower Black Shield brings together premium matte-black design, advanced inverter efficiency and natural R290 refrigerant in one quiet, smart all-in-one system — purpose-built for modern Kiwi homes.
          </p>
          <ul className="mt-6 space-y-2.5 text-[#475569]">
            {[
              "DC inverter compressor for adaptive efficiency",
              "Natural R290 refrigerant — low global warming impact",
              "Free Wi-Fi app for scheduling and monitoring",
              "Backed by a 7-year tank warranty*",
            ].map((t, i) => (
              <li key={i} className="flex items-center gap-2.5">
                <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-[#10B981]/15 text-[#10B981] shrink-0">
                  <BadgeCheck className="h-3 w-3" strokeWidth={2.5} />
                </span>
                {t}
              </li>
            ))}
          </ul>
          <Link
            to="/neopower-black-shield"
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#0B132B] hover:bg-[#050914] text-white px-7 py-3.5 font-semibold transition-all"
          >
            Explore Black Shield Series <ArrowRight className="h-4 w-4" />
          </Link>
        </Reveal>

        {/* Premium visual — architectural lifestyle product hero */}
        <Reveal delay={120} className="lg:col-span-6">
          <div className="relative">
            {/* Outer ambient color glow */}
            <div className="absolute -inset-8 rounded-[2.75rem] bg-[radial-gradient(60%_50%_at_70%_30%,rgba(16,185,129,0.18),transparent_60%),radial-gradient(50%_60%_at_20%_80%,rgba(11,19,43,0.10),transparent_60%)] blur-2xl pointer-events-none" />

            {/* Framed premium lifestyle photo */}
            <div className="relative rounded-[2rem] overflow-hidden ring-1 ring-[#E2E8F0] shadow-[0_40px_100px_-30px_rgba(11,19,43,0.35)] bg-[#0B132B]">
              <img
                src="/products/neopower_innovation.png"
                alt="NeoPower Black Shield installed at a premium modern home"
                className="w-full h-[460px] sm:h-[560px] object-cover"
                loading="lazy"
              />

              {/* Top-left brand chip */}
              <span className="absolute top-5 left-5 z-[2] inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/15 backdrop-blur-md border border-white/25 text-[10px] tracking-[0.2em] uppercase font-semibold text-white shadow-sm">
                <span className="h-1.5 w-1.5 rounded-full bg-[#10B981] animate-pulse" />
                NeoPower
              </span>
              {/* Top-right tech chip */}
              <span className="absolute top-5 right-5 z-[2] inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#0B132B]/70 backdrop-blur-md border border-white/15 text-[10px] tracking-[0.18em] uppercase font-semibold text-white">
                R290 · Inverter
              </span>

              {/* Bottom gradient + credibility band */}
              <div className="absolute inset-x-0 bottom-0 p-5 sm:p-6 bg-gradient-to-t from-[#0B132B]/95 via-[#0B132B]/70 to-transparent">
                <p className="text-[10px] uppercase tracking-[0.25em] text-white/65 font-semibold">Backed by</p>
                <p className="font-heading text-2xl sm:text-3xl font-bold text-white mt-1 leading-tight">
                  10+ <span className="text-[#10B981]">Years</span>
                </p>
                <p className="text-xs sm:text-sm text-white/75 mt-1">
                  Smart home & energy-efficiency experience across NZ & Australia.
                </p>

                <div className="mt-4 grid grid-cols-4 gap-2">
                  {[
                    { v: "5.4", l: "COP up to" },
                    { v: "80%", l: "Save up to" },
                    { v: "42 dBa", l: "Silent" },
                    { v: "7 yr", l: "Warranty*" },
                  ].map((s, i) => (
                    <div key={i} className="rounded-xl bg-white/10 backdrop-blur border border-white/15 px-2.5 py-2">
                      <p className="text-[9px] uppercase tracking-widest text-white/65 font-semibold leading-none">{s.l}</p>
                      <p className="font-heading text-sm sm:text-base font-bold mt-1 num-tab text-white leading-none">{s.v}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
